import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Heart, MessageSquare, Edit3, Trash2, X, Loader2, BadgeCheck } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ReviewCard — displays a review with:
 *  - Like (heart) button for any signed-in viewer (toggle, optimistic)
 *  - Response thread visible to all, but writable ONLY by the review recipient (the reviewed Hooman)
 *
 * Props:
 *   review: { id, reviewer, rating, text, created_at, like_count, liked_by_me, response, hooman_id, ... }
 *   isRecipient: boolean — true when the viewer is the Hooman being reviewed
 *   onChange?: () => void — called after server state changes
 */
export default function ReviewCard({ review: r, isRecipient, onChange }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!r.liked_by_me);
  const [count, setCount] = useState(r.like_count || 0);
  const [busyLike, setBusyLike] = useState(false);
  const [editing, setEditing] = useState(false);
  const [respText, setRespText] = useState(r.response?.text || "");
  const [respBusy, setRespBusy] = useState(false);
  const [respErr, setRespErr] = useState("");
  const [response, setResponse] = useState(r.response || null);

  const toggleLike = async () => {
    if (!user) return;
    setBusyLike(true);
    // optimistic
    const next = !liked;
    setLiked(next);
    setCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      const { data } = await api.post(`/reviews/${r.id}/like`);
      setLiked(!!data.liked);
      setCount(data.like_count || 0);
    } catch (e) {
      // rollback
      setLiked(liked);
      setCount(count);
    } finally { setBusyLike(false); }
  };

  const saveResponse = async (e) => {
    e?.preventDefault?.();
    setRespBusy(true); setRespErr("");
    try {
      const { data } = await api.post(`/reviews/${r.id}/response`, { text: respText });
      setResponse(data.response);
      setEditing(false);
      onChange?.();
    } catch (e) { setRespErr(formatErr(e)); }
    finally { setRespBusy(false); }
  };

  const deleteResponse = async () => {
    if (!window.confirm("Remove your response?")) return;
    setRespBusy(true);
    try {
      await api.delete(`/reviews/${r.id}/response`);
      setResponse(null);
      setRespText("");
      setEditing(false);
      onChange?.();
    } catch (e) { setRespErr(formatErr(e)); }
    finally { setRespBusy(false); }
  };

  return (
    <div className="card p-6" data-testid={`review-${r.id}`}>
      <div className="flex items-center gap-3">
        {r.reviewer?.avatar && (
          <img src={r.reviewer.avatar} className="h-9 w-9 rounded-full object-cover" alt=""/>
        )}
        <div className="flex-1">
          <p className="font-medium">{r.reviewer?.name}</p>
          <p className="text-xs text-[#8A8A8A]">{new Date(r.created_at).toLocaleDateString()}</p>
        </div>
        <span className="inline-flex items-center gap-1 text-[#0057FF]">
          {Array.from({ length: r.rating }).map((_, i) => (
            <Star key={i} size={14} className="fill-[#0057FF] stroke-[#0057FF]" />
          ))}
        </span>
      </div>

      <p className="mt-3 text-[#0A0A0A] whitespace-pre-wrap">{r.text}</p>

      {/* Like row */}
      <div className="mt-4 flex items-center gap-1">
        <button
          type="button"
          disabled={!user || busyLike}
          onClick={toggleLike}
          data-testid={`review-like-${r.id}`}
          title={user ? (liked ? "Unlike" : "Like") : "Sign in to like"}
          className={`group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-all border
            ${liked
              ? "bg-[#EEF3FF] text-[#0057FF] border-[#F5C9BB] hover:bg-[#FCE4DA]"
              : "bg-white text-[#4A4A4A] border-[#E5E5E3] hover:bg-[#F2F2F0]"}
            ${(!user || busyLike) ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          <Heart
            size={14}
            className={`transition-transform ${liked ? "fill-[#0057FF] stroke-[#0057FF] scale-110" : "stroke-current"}`}
          />
          <span data-testid={`review-like-count-${r.id}`}>{count}</span>
          {liked && <span className="text-xs">· Liked</span>}
        </button>

        {response && !editing && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#8A8A8A]">
            <MessageSquare size={12}/> Response from {r.reviewer ? "the Hooman" : "owner"}
          </span>
        )}

        {/* Recipient-only response CTA */}
        {isRecipient && !response && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`review-respond-open-${r.id}`}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm bg-[#0A0A0A] text-white hover:bg-[#1F1F1F]"
          >
            <MessageSquare size={12}/> Respond
          </button>
        )}
      </div>

      {/* Existing response display */}
      {response && !editing && (
        <div className="mt-4 pl-4 border-l-2 border-[#E5E5E3]" data-testid={`review-response-${r.id}`}>
          <div className="flex items-center gap-1.5 text-xs text-[#4A4A4A] mb-1">
            <BadgeCheck size={12} className="text-[#0A0A0A]"/>
            <span className="font-medium">{r.hooman?.name || "Recipient"}</span>
            <span>·</span>
            <span>
              {new Date(response.updated_at || response.created_at).toLocaleDateString()}
              {response.updated_at && response.updated_at !== response.created_at && (
                <span className="ml-1 italic">(edited)</span>
              )}
            </span>
          </div>
          <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap" data-testid={`review-response-text-${r.id}`}>
            {response.text}
          </p>
          {isRecipient && (
            <div className="mt-2 flex gap-1">
              <button
                type="button"
                onClick={() => { setRespText(response.text); setEditing(true); }}
                className="inline-flex items-center gap-1 text-xs text-[#4A4A4A] hover:text-[#0A0A0A]"
                data-testid={`review-response-edit-${r.id}`}
              >
                <Edit3 size={12}/> Edit
              </button>
              <span className="text-[#E5E5E3]">·</span>
              <button
                type="button"
                onClick={deleteResponse}
                disabled={respBusy}
                className="inline-flex items-center gap-1 text-xs text-[#B84B4B] hover:text-[#7A2E2E]"
                data-testid={`review-response-delete-${r.id}`}
              >
                <Trash2 size={12}/> Delete
              </button>
            </div>
          )}
        </div>
      )}

      {/* Response editor (recipient only) */}
      {isRecipient && editing && (
        <form onSubmit={saveResponse} className="mt-4 pl-4 border-l-2 border-[#0A0A0A]"
              data-testid={`review-response-form-${r.id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="overline">{response ? "Edit your response" : "Public response"}</p>
            <button type="button" onClick={() => setEditing(false)} className="text-[#8A8A8A] hover:text-[#0A0A0A]"
                    data-testid={`review-response-cancel-${r.id}`}>
              <X size={14}/>
            </button>
          </div>
          <textarea
            required
            minLength={2}
            maxLength={1000}
            value={respText}
            onChange={(e) => setRespText(e.target.value)}
            placeholder="Thank them, clarify, or share more context. Visible to everyone."
            className="input"
            rows={3}
            data-testid={`review-response-textarea-${r.id}`}
          />
          {respErr && <p className="text-sm text-[#B84B4B] mt-2">{respErr}</p>}
          <div className="flex gap-2 mt-2">
            <button
              type="submit"
              disabled={respBusy || respText.trim().length < 2}
              className="btn-primary !py-2 !px-4 text-sm"
              data-testid={`review-response-save-${r.id}`}
            >
              {respBusy ? <><Loader2 size={12} className="animate-spin"/> Saving…</> : (response ? "Update response" : "Post response")}
            </button>
            <Link
              to={`/messages/${r.reviewer_id}`}
              className="btn-ghost text-sm"
              data-testid={`review-response-dm-${r.id}`}
            >
              Send a private DM instead
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
